"use client";

import { useContext, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useUser } from "@clerk/nextjs";

import type { DateInfo, EventMetadata, SimilarityDetails } from "@soonlist/cal";
import type {
  AddToCalendarButtonPropsRestricted,
  ATCBActionEventConfig,
} from "@soonlist/cal/types";
import type { Comment, EventFollow, List, User } from "@soonlist/validators";
import {
  eventTimesAreDefined,
  formatRelativeTime,
  getDateInfoUTC,
  getDateTimeInfo,
  shouldShowYear,
} from "@soonlist/cal";

import type { AddToCalendarCardProps } from "./AddToCalendarCard";
import type { EventWithUser } from "./EventList";
import {
  Archive,
  Copy,
  Globe,
  Instagram,
  Lock,
  MapPin,
  ScanText,
} from "~/components/icons";
import { TimezoneContext } from "~/context/TimezoneContext";
import { DEFAULT_TIMEZONE } from "~/lib/constants";
import { getGoogleMapsUrl } from "~/lib/maps";
import { buildDisplayImageUrl, cn, formatUrlForDisplay } from "~/lib/utils";
import { CalendarButton } from "./CalendarButton";
import { ArchiveButton, DeleteButton, RestoreButton } from "./DeleteButton";
import { EditButton } from "./EditButton";
import EventCard from "./EventCard";
import { FollowEventButton } from "./FollowButtons";
import { buildDefaultUrl } from "./ImageUpload";
import { Logo } from "./Logo";
import { PrivateVisibilityBadge } from "./PrivateVisibilityBadge";
import { SaveButton } from "./SaveButton";
import { ShareButton } from "./ShareButton";
import { SubscribeCTA } from "./SubscribeCTA";
import { UserAvatarMini } from "./UserAvatarMini";

function getPlatformUrl(
  platform: string | undefined,
  username: string,
): string | null {
  const trimmedUsername = username.trim();
  if (
    trimmedUsername.startsWith("http://") ||
    trimmedUsername.startsWith("https://")
  ) {
    try {
      new URL(trimmedUsername);
      return trimmedUsername;
    } catch {}
  }

  const cleanUsername = trimmedUsername.replace(/^@/, "");

  switch (platform?.toLowerCase()) {
    case "tiktok":
      return `https://tiktok.com/@${cleanUsername}`;
    case "twitter":
      return `https://twitter.com/${cleanUsername}`;
    case "facebook":
      return `https://facebook.com/${cleanUsername}`;
    case "instagram":
      return `https://instagram.com/${cleanUsername}`;
    default:
      return null;
  }
}

export function EventMetadataDisplay({
  eventMetadata,
}: {
  eventMetadata: EventMetadata;
}) {
  const sourceUrls = eventMetadata.sourceUrls || [];
  const hasSourceUrls = sourceUrls.length > 0;
  const hasMentions = !!(
    eventMetadata.mentions && eventMetadata.mentions.length > 0
  );
  if (!hasSourceUrls && !hasMentions) return null;

  const firstMentionCandidate = hasMentions
    ? (eventMetadata.mentions || [])[0]
    : undefined;
  const isInstagram = eventMetadata.platform === "instagram";

  return (
    <div className="mt-3 flex flex-wrap items-center gap-1 text-sm text-neutral-2">
      {hasSourceUrls && (
        <>
          <span>link:</span>
          {sourceUrls.map((url, index) => (
            <span key={`${url}-${index}`} className="flex items-center">
              <Link
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="break-all text-interactive-1 hover:underline"
              >
                {formatUrlForDisplay(url)}
              </Link>
              {index < sourceUrls.length - 1 && <span>, </span>}
            </span>
          ))}
          {hasMentions && <span className="mx-1">•</span>}
        </>
      )}

      {hasMentions && firstMentionCandidate && (
        <>
          <span>via</span>
          {(() => {
            const platformUrl = getPlatformUrl(
              eventMetadata.platform,
              firstMentionCandidate,
            );
            return platformUrl ? (
              <Link
                href={platformUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-0.5 text-interactive-1 hover:underline"
              >
                {isInstagram && (
                  <Instagram className="mt-[3px] size-3 flex-shrink-0" />
                )}
                {firstMentionCandidate}
              </Link>
            ) : (
              <span className="text-neutral-1">{firstMentionCandidate}</span>
            );
          })()}
          {(eventMetadata.mentions || []).length > 1 && (
            <>
              <span>with</span>
              {(eventMetadata.mentions || []).slice(1).map((mention, index) => (
                <span key={mention} className="flex items-center">
                  {(() => {
                    const platformUrl = getPlatformUrl(
                      eventMetadata.platform,
                      mention,
                    );
                    return platformUrl ? (
                      <Link
                        href={platformUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-0.5 text-interactive-1 hover:underline"
                      >
                        {isInstagram && (
                          <Instagram className="mt-[3px] size-3 flex-shrink-0" />
                        )}
                        {mention}
                      </Link>
                    ) : (
                      <span className="text-neutral-1">{mention}</span>
                    );
                  })()}
                  {index < (eventMetadata.mentions || []).length - 2 && (
                    <span>, </span>
                  )}
                </span>
              ))}
            </>
          )}
        </>
      )}
    </div>
  );
}

interface EventListItemProps {
  list?: List;
  variant?: "card" | "minimal";
  user?: User;
  eventFollows: EventFollow[];
  comments: Comment[];
  id: string;
  createdAt?: Date | string;
  event: AddToCalendarCardProps;
  visibility: "public" | "private";
  hideCurator?: boolean;
  showOtherCurators?: boolean;
  similarEvents?: {
    event: EventWithUser;
    similarityDetails: SimilarityDetails;
  }[];
  filePath?: string;
  happeningNow?: boolean;
  lists?: List[];
  index?: number;
}

interface EventPageProps {
  user?: User;
  eventFollows: EventFollow[];
  comments: Comment[];
  id: string;
  createdAt?: Date | string;
  event: AddToCalendarButtonPropsRestricted;
  image?: string | null;
  visibility: "public" | "private";
  singleEvent?: boolean;
  hideCurator?: boolean;
  showOtherCurators?: boolean;
  similarEvents?: {
    event: EventWithUser;
    similarityDetails: SimilarityDetails;
  }[];
  lists?: List[];
  children?: React.ReactNode;
  eventMetadata?: EventMetadata;
  archivedAt?: string | null;
  hideSubscribeCTA?: boolean;
}

function EventDateDisplaySimple({
  startDate,
  startTime,
  endDate,
  timezone,
}: {
  startDate?: string;
  startTime?: string;
  endDate?: string;
  endTime?: string;
  timezone: string;
}) {
  const { timezone: userTimezone } = useContext(TimezoneContext);
  if (!startDate || !endDate) {
    console.error("startDate or endDate is missing");
    return null;
  }

  if (!timezone) {
    console.error("timezone is missing");
    return null;
  }

  const startDateInfo = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, userTimezone.toString())
    : getDateInfoUTC(startDate);

  return (
    <div className="flex flex-col items-center justify-center gap-3">
      <div
        className="text-lg font-semibold uppercase leading-none text-neutral-2"
        suppressHydrationWarning
      >
        {startDateInfo?.monthName.substring(0, 3)}
      </div>
      <div
        className="font-heading text-4xl font-bold leading-none tracking-tighter text-neutral-1"
        suppressHydrationWarning
      >
        {startDateInfo?.day}
      </div>
    </div>
  );
}

function EventDetailsCard({
  id,
  name,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
  location,
  description,
  visibility,
  noLinks = false,
}: {
  id: string;
  name: string;
  image?: string;
  startTime: string;
  startDate: string;
  endTime: string;
  endDate: string;
  timezone: string;
  description?: string;
  location?: string;
  visibility: "public" | "private";
  noLinks?: boolean;
}) {
  const { timezone: userTimezone } = useContext(TimezoneContext);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!startDate || !endDate) {
    console.error("startDate or endDate is missing");
    return null;
  }

  if (!timezone) {
    console.error("timezone is missing");
    return null;
  }

  const startDateInfo = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, userTimezone.toString())
    : getDateInfoUTC(startDate);
  const endDateInfo = endTime
    ? getDateTimeInfo(endDate, endTime, timezone, userTimezone.toString())
    : getDateInfoUTC(endDate);

  const eventTimezoneStartDateInfo: DateInfo | null = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, timezone)
    : null;
  const eventTimezoneEndDateInfo: DateInfo | null = endTime
    ? getDateTimeInfo(endDate, endTime, timezone, timezone)
    : null;

  if (!startDateInfo || !endDateInfo) {
    console.error("startDateInfo or endDateInfo is missing");
    return null;
  }

  if (!description) {
    description = "";
  }

  return (
    <div className="flex w-full flex-col items-start justify-center gap-2">
      <DateAndTimeDisplay
        endDateInfo={endDateInfo}
        endTime={endTime}
        isClient={isClient}
        startDateInfo={startDateInfo}
        startTime={startTime}
        variant="compact"
        eventTimezone={timezone}
        userTimezone={userTimezone.toString()}
        eventTimezoneStartDateInfo={eventTimezoneStartDateInfo || undefined}
        eventTimezoneEndDateInfo={eventTimezoneEndDateInfo || undefined}
      />
      <div className="flex w-full flex-col items-start gap-2">
        {noLinks ? (
          <h2 className="line-clamp-3 pr-12 text-2xl font-bold leading-9 tracking-wide text-interactive-1">
            {name}
          </h2>
        ) : (
          <Link
            href={`/event/${id}`}
            className={
              "line-clamp-3 pr-12 text-2xl font-bold leading-9 tracking-wide text-interactive-1"
            }
          >
            {name}
          </Link>
        )}
        <div className="flex-start flex flex-wrap gap-2 pr-12 text-lg font-medium leading-none">
          {location && (
            <>
              {noLinks ? (
                <div className="line-clamp-1 flex shrink items-center gap-0.5 break-all text-neutral-2">
                  <MapPin className="size-4 flex-shrink-0" />
                  <span className="line-clamp-1">{location}</span>
                </div>
              ) : (
                <Link
                  href={getGoogleMapsUrl(location)}
                  className="line-clamp-1 flex shrink items-center gap-0.5 break-all text-neutral-2"
                >
                  <MapPin className="size-4 flex-shrink-0" />
                  <span className="line-clamp-1">{location}</span>
                </Link>
              )}
            </>
          )}
          {visibility === "private" ? <PrivateVisibilityBadge /> : null}
        </div>
      </div>
    </div>
  );
}

function EventDetails({
  id,
  name,
  startDate,
  startTime,
  endDate,
  endTime,
  timezone,
  location,
  description,
  preview,
  EventActionButtons,
  happeningNow,
  visibility,
}: {
  id: string;
  name: string;
  startTime: string;
  startDate: string;
  endTime: string;
  endDate: string;
  timezone: string;
  description?: string;
  location?: string;
  EventActionButtons?: React.ReactNode;
  preview?: boolean;
  variant?: "minimal";
  happeningNow?: boolean;
  visibility: "public" | "private";
}) {
  const { timezone: userTimezone } = useContext(TimezoneContext);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!startDate || !endDate) {
    console.error("startDate or endDate is missing");
    return null;
  }

  if (!timezone) {
    console.error("timezone is missing");
    return null;
  }

  const startDateInfo = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, userTimezone.toString())
    : getDateInfoUTC(startDate);
  const endDateInfo = endTime
    ? getDateTimeInfo(endDate, endTime, timezone, userTimezone.toString())
    : getDateInfoUTC(endDate);

  const eventTimezoneStartDateInfo = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, timezone)
    : null;
  const eventTimezoneEndDateInfo = endTime
    ? getDateTimeInfo(endDate, endTime, timezone, timezone)
    : null;

  if (!startDateInfo || !endDateInfo) {
    console.error("startDateInfo or endDateInfo is missing");
    return null;
  }

  if (!description) {
    description = "";
  }

  return (
    <div className="relative">
      <div className="mb-2 flex items-center">
        <DateAndTimeDisplay
          endDateInfo={endDateInfo}
          endTime={endTime}
          isClient={isClient}
          startDateInfo={startDateInfo}
          startTime={startTime}
          happeningNow={happeningNow}
          variant="compact"
          eventTimezone={timezone}
          userTimezone={userTimezone.toString()}
          eventTimezoneStartDateInfo={eventTimezoneStartDateInfo || undefined}
          eventTimezoneEndDateInfo={eventTimezoneEndDateInfo || undefined}
        />
      </div>
      <div className="">
        <Link
          href={preview ? "" : `/event/${id}`}
          className={
            "line-clamp-2 pb-1 text-lg font-bold leading-tight text-neutral-1"
          }
        >
          {name}
        </Link>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {location && (
            <Link
              href={getGoogleMapsUrl(location)}
              className="line-clamp-1 break-all text-neutral-2"
            >
              <MapPin className="mr-0.5 inline size-4" />
              <span className="inline">{location}</span>
            </Link>
          )}
          {visibility === "private" ? <PrivateVisibilityBadge /> : null}
        </div>

        <div className="absolute bottom-2 right-2 z-10">
          {EventActionButtons}
        </div>
      </div>
    </div>
  );
}

function HappeningSoonBadge({ startDateInfo }: { startDateInfo: DateInfo }) {
  const relativeTimeString = formatRelativeTime(startDateInfo);
  if (!relativeTimeString) {
    return null;
  }

  const now = new Date();
  const startDateObj = new Date(
    startDateInfo.year,
    startDateInfo.month - 1,
    startDateInfo.day,
    startDateInfo.hour,
    startDateInfo.minute,
  );
  const difference = startDateObj.getTime() - now.getTime();

  if (difference < 0) {
    return null;
  }

  return (
    <span className="ml-1 whitespace-nowrap rounded-full bg-accent-yellow px-1 text-interactive-1">{`${relativeTimeString}`}</span>
  );
}

function EventActionButtons({
  user,
  event,
  id,
  isOwner,
  isFollowing,
  visibility,
  variant,
  size,
}: {
  user?: User;
  event: AddToCalendarButtonPropsRestricted;
  id: string;
  isOwner: boolean;
  isFollowing?: boolean;
  visibility: "public" | "private";
  variant?: "none" | "minimal";
  size?: "sm";
}) {
  if (!user) {
    return null;
  }
  if (variant === "none") {
    return <></>;
  }

  if (variant === "minimal") {
    const scale =
      size === "sm" ? "transform scale-[0.55] origin-bottom-right" : "";
    return (
      <div className={cn("flex w-full flex-wrap items-center gap-3", scale)}>
        <ShareButton type="icon" event={event} id={id} />
        <CalendarButton
          type="icon"
          event={event as ATCBActionEventConfig}
          id={id}
          username={user.username}
          userDisplayName={user.displayName}
        />
        {!isOwner && (
          <FollowEventButton eventId={id} following={isFollowing} type="icon" />
        )}
        {isOwner && (
          <>
            <EditButton type="icon" userId={user.id} id={id} />
            <ArchiveButton type="icon" userId={user.id} id={id} />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="flex w-full flex-wrap items-center gap-3">
      <div className="flex grow items-center justify-between">
        {visibility !== "private" && (
          <Link
            className="text-lg font-medium leading-none text-neutral-2"
            href={`/${user.username}`}
          >
            added by @{user.username}
          </Link>
        )}
        {visibility === "private" && (
          <div>
            <PrivateVisibilityBadge className="text-sm" />
          </div>
        )}
        <Link
          href={`/${user.username}`}
          className="box-content block size-[2.625rem] shrink-0 rounded-full border-4 border-accent-yellow"
        >
          <Image
            className="rounded-full"
            src={user.userImage}
            alt=""
            width={375}
            height={375}
          />
        </Link>
      </div>
      <ShareButton type="icon" event={event} id={id} />
      <CalendarButton
        type="icon"
        event={event as ATCBActionEventConfig}
        id={id}
        username={user.username}
      />
      {!isOwner && (
        <FollowEventButton eventId={id} following={isFollowing} type="icon" />
      )}
      {isOwner && (
        <>
          <EditButton type="icon" userId={user.id} id={id} />
          <ArchiveButton type="icon" userId={user.id} id={id} />
        </>
      )}
    </div>
  );
}

export function EventListItem(props: EventListItemProps) {
  const { user: clerkUser } = useUser();
  const { user, eventFollows, id, event, filePath, visibility } = props;
  const { timezone: userTimezone } = useContext(TimezoneContext);
  const roles = clerkUser?.publicMetadata.roles as string[] | undefined;
  const isSelf = clerkUser?.id === user?.id;
  const isOwner = isSelf || roles?.includes("admin");
  const isFollowing = !!eventFollows.find(
    (item) => item.userId === clerkUser?.id,
  );
  const isPrivate = visibility === "private";
  const image =
    event.images?.[3] ||
    (filePath ? buildDefaultUrl(props.filePath || "") : undefined);

  if (!props.variant || props.variant === "minimal") {
    const startDateInfo = event.startTime
      ? getDateTimeInfo(
          event.startDate || "",
          event.startTime || "",
          event.timeZone || DEFAULT_TIMEZONE,
          userTimezone,
        )
      : getDateInfoUTC(event.startDate || "");
    const endDateInfo = event.endTime
      ? getDateTimeInfo(
          event.endDate || event.startDate || "",
          event.endTime || event.startTime || "",
          event.timeZone || DEFAULT_TIMEZONE,
          userTimezone,
        )
      : getDateInfoUTC(event.endDate || event.startDate || "");

    const relativeTime = startDateInfo ? formatRelativeTime(startDateInfo) : "";
    const isHappeningNow =
      props.happeningNow ?? relativeTime === "Happening now";

    const isRecent = (() => {
      if (!props.createdAt) return false;
      const threeHoursAgo = Date.now() - 3 * 60 * 60 * 1000;
      const createdAtTs = new Date(props.createdAt).getTime();
      if (Number.isNaN(createdAtTs)) return false;
      return createdAtTs > threeHoursAgo;
    })();

    const thumbWidth = 94;
    const thumbHeight = Math.round((thumbWidth * 16) / 9);
    const imageRotation =
      props.index !== undefined
        ? props.index % 2 === 0
          ? "10deg"
          : "-10deg"
        : Number(id?.charCodeAt(0) || 0) % 2 === 0
          ? "10deg"
          : "-10deg";

    const baseBorderColor = "#E9E3FF";
    const happeningBorderColor = "#FEEA9F";
    const recentGlowColor = "#E0D9FF";
    const cardBorderColor = isRecent
      ? recentGlowColor
      : isHappeningNow
        ? happeningBorderColor
        : baseBorderColor;

    const cardShadowRadius = isRecent ? 8 : 2.5;

    function formatFullTimeRange(
      start: DateInfo,
      end: DateInfo,
    ): { primary: string; secondary?: string } {
      const resolveTZAbbr = (tz?: string): string | undefined => {
        try {
          if (!tz) return undefined;
          const parts = new Intl.DateTimeFormat("en-US", {
            timeZone: tz,
            timeZoneName: "short",
          }).formatToParts(new Date());
          const val = parts.find((p) => p.type === "timeZoneName")?.value;
          return val?.trim() ? val : undefined;
        } catch {
          return undefined;
        }
      };
      const toHour = (h: number) => (h % 12 === 0 ? 12 : h % 12);
      const toMinute = (m: number) => m.toString().padStart(2, "0");
      const startHour = toHour(start.hour);
      const endHour = toHour(end.hour);
      const startMin = toMinute(start.minute);
      const endMin = toMinute(end.minute);
      const startPeriod = start.hour < 12 ? "AM" : "PM";
      const endPeriod = end.hour < 12 ? "AM" : "PM";

      const normalizeTimezone = (tz?: string): string => {
        const val = tz?.trim().toLowerCase();
        return !val || val === "unknown" ? "" : val;
      };

      const normalizedEventTz = normalizeTimezone(event.timeZone);
      const normalizedUserTz = normalizeTimezone(userTimezone);

      const shouldShowTimezone =
        normalizedEventTz &&
        normalizedUserTz &&
        normalizedEventTz !== normalizedUserTz &&
        event.startTime;

      const timeStr = `${startHour}:${startMin}${startPeriod} - ${endHour}:${endMin}${endPeriod}`;

      if (
        shouldShowTimezone &&
        event.startDate &&
        event.startTime &&
        event.endTime
      ) {
        const timezoneAbbreviation: string | undefined = resolveTZAbbr(
          event.timeZone || "",
        );
        if (timezoneAbbreviation) {
          const eventStartDateInfo = getDateTimeInfo(
            event.startDate || "",
            event.startTime || "",
            event.timeZone || "",
            event.timeZone || "",
          );
          const eventEndDateInfo = getDateTimeInfo(
            event.endDate || event.startDate || "",
            event.endTime || event.startTime || "",
            event.timeZone || "",
            event.timeZone || "",
          );

          if (eventStartDateInfo && eventEndDateInfo) {
            const eventStartHour = toHour(eventStartDateInfo.hour);
            const eventStartMin = toMinute(eventStartDateInfo.minute);
            const eventEndHour = toHour(eventEndDateInfo.hour);
            const eventEndMin = toMinute(eventEndDateInfo.minute);
            const eventStartPeriod = eventStartDateInfo.hour < 12 ? "AM" : "PM";
            const eventEndPeriod = eventEndDateInfo.hour < 12 ? "AM" : "PM";

            const eventTimeStr = `${eventStartHour}:${eventStartMin}${eventStartPeriod} - ${eventEndHour}:${eventEndMin}${eventEndPeriod}`;
            return {
              primary: timeStr,
              secondary: `${eventTimeStr} ${timezoneAbbreviation}`,
            };
          }
        }
      }

      return { primary: timeStr };
    }

    const { dateText, secondaryTimeText } = (() => {
      if (!startDateInfo || !endDateInfo) {
        return { dateText: "", secondaryTimeText: undefined };
      }
      const dateStr = `${startDateInfo.dayOfWeek.substring(0, 3)}, ${startDateInfo.monthName} ${startDateInfo.day}${
        shouldShowYear(startDateInfo) ? `, ${startDateInfo.year}` : ""
      }`;
      const { primary, secondary } = formatFullTimeRange(
        startDateInfo,
        endDateInfo,
      );
      return {
        dateText: `${dateStr} • ${primary}`,
        secondaryTimeText: secondary,
      };
    })();

    const relativeLabel = (() => {
      if (!relativeTime) return "";
      if (props.happeningNow) return "Happening now";
      return relativeTime;
    })();

    const atcbEvent: ATCBActionEventConfig = {
      name: event.name,
      description: event.description,
      startDate: event.startDate,
      startTime: event.startTime,
      endDate: event.endDate,
      endTime: event.endTime,
      timeZone: event.timeZone,
      location: event.location,
    };

    return (
      <li className="relative">
        <Link
          href={`/event/${id}`}
          className="absolute -right-2 top-1/2 z-10"
          style={{
            transform: `translateY(-50%) rotate(${imageRotation})`,
          }}
        >
          <div
            style={{
              width: thumbWidth,
              height: thumbHeight,
              borderRadius: 20,
              overflow: "hidden",
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(90,50,251,0.18)",
            }}
          >
            {image ? (
              <Image
                src={buildDisplayImageUrl(image) ?? image}
                alt=""
                width={thumbWidth}
                height={thumbHeight}
                className="object-cover"
                style={{
                  width: thumbWidth,
                  height: thumbHeight,
                  borderRadius: 20,
                  borderWidth: 3,
                  borderColor: "white",
                  objectPosition: "top",
                  imageOrientation: "from-image",
                }}
              />
            ) : (
              <div
                className="border border-purple-300 bg-interactive-3"
                style={{
                  width: thumbWidth,
                  height: thumbHeight,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 20,
                  borderWidth: 3,
                  borderColor: "white",
                }}
              />
            )}
          </div>
        </Link>

        <div
          className="my-1 mt-4 rounded-[20px] bg-white p-3"
          style={{
            paddingRight: thumbWidth * 0.7,
            marginRight: 16,
            borderWidth: 2,
            borderColor: cardBorderColor,
            boxShadow: `0 2px ${cardShadowRadius + 2}px rgba(90,50,251,0.12)`,
          }}
        >
          <Link href={`/event/${id}`} className="block">
            <div className="mb-1">
              <p className="text-sm font-medium text-neutral-2">{dateText}</p>
              {secondaryTimeText ? (
                <div className="flex flex-row items-center gap-1">
                  <Globe size={12} color="#627496" className="flex-shrink-0" />
                  <span className="text-xs font-medium italic text-neutral-2">
                    {secondaryTimeText}
                  </span>
                </div>
              ) : null}
            </div>

            <div className="mb-1">
              <h3 className="truncate text-lg font-bold text-neutral-1">
                {event.name}
              </h3>
              {event.shortDescription ? (
                <div className="-mt-0.5 flex flex-row items-center gap-1">
                  <ScanText
                    size={12}
                    color="#5A32FB"
                    className="flex-shrink-0"
                  />
                  <span className="flex-1 truncate text-sm font-medium italic text-interactive-1">
                    {event.shortDescription}
                  </span>
                </div>
              ) : null}
            </div>

            {event.location && (
              <div className="mb-1">
                <p className="truncate text-sm text-neutral-2">
                  {event.location}
                </p>
              </div>
            )}
          </Link>

          <div className="-mb-2 mt-0 flex flex-row flex-wrap items-center gap-x-3 gap-y-1.5">
            <div className="flex flex-row items-center gap-3">
              <SaveButton
                eventId={id}
                event={event}
                userId={clerkUser?.id}
                eventUserId={user?.id}
                isSaved={isFollowing}
                className="-ml-2"
              />

              <CalendarButton
                type={"icon"}
                event={atcbEvent}
                id={id}
                username={user?.username}
                userDisplayName={user?.displayName}
              />

              {!isOwner && (
                <FollowEventButton
                  eventId={id}
                  following={isFollowing}
                  type="icon"
                />
              )}

              {user && isOwner && (
                <>
                  <EditButton type="icon" userId={user.id} id={id} />
                  <ArchiveButton type="icon" userId={user.id} id={id} />
                </>
              )}
            </div>

            {isPrivate ||
            (isOwner &&
              props.similarEvents &&
              props.similarEvents.length > 0) ? (
              <div className="ml-auto flex shrink-0 flex-row items-center justify-end gap-1.5">
                {isPrivate ? (
                  <span
                    className="flex flex-row items-center rounded-full bg-neutral-3/40 px-1.5 py-0.5"
                    aria-label="Private event"
                  >
                    <Lock size={11} color="#627496" />
                  </span>
                ) : null}

                {isOwner &&
                props.similarEvents &&
                props.similarEvents.length > 0 ? (
                  <span className="flex flex-row items-center gap-0.5 rounded-full bg-neutral-3/40 px-1.5 py-0.5">
                    <Copy size={11} color="#627496" />
                    <span className="text-xs font-medium text-neutral-2">
                      {props.similarEvents.length}
                    </span>
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        {user && !isSelf && (props.showOtherCurators || !props.hideCurator) && (
          <div className="mx-1 mt-2 flex items-center justify-center gap-2">
            <UserAvatarMini
              username={user.username}
              displayName={user.displayName}
              userImage={user.userImage}
              isSupporter={user.isSupporter}
            />
          </div>
        )}

        <div className="pointer-events-none absolute left-0 right-0 top-0 z-20 flex items-center justify-center gap-2">
          {isRecent && (
            <div
              className="rounded-full px-2 py-0.5"
              style={{
                borderWidth: 2,
                borderColor: "white",
                backgroundColor: "#E0D9FF",
              }}
            >
              <span className="text-[10px] font-medium text-neutral-1">
                New
              </span>
            </div>
          )}
          {relativeLabel && (
            <div
              className="rounded-full px-2 py-0.5"
              style={{
                borderWidth: 2,
                borderColor: "white",
                backgroundColor: "#FEEA9F",
              }}
            >
              <span className="text-[10px] font-medium text-neutral-1">
                {relativeLabel}
              </span>
            </div>
          )}
        </div>
      </li>
    );
  }

  return (
    <li
      className={cn(
        "relative h-full overflow-hidden rounded-xl bg-white shadow-sm after:pointer-events-none after:absolute after:left-0 after:top-0 after:size-full after:rounded-xl after:border after:border-neutral-3 after:shadow-sm",
      )}
    >
      <Link href={`/event/${id}`} className="block">
        {image && (
          <div className="relative h-44 w-full grow">
            <Image
              className="rounded-t-xl object-cover"
              src={buildDisplayImageUrl(image) ?? image}
              alt=""
              fill
              sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
              style={{ imageOrientation: "from-image" }}
              priority
            />
          </div>
        )}
        {!image && (
          <div className="relative h-44 w-full grow bg-accent-yellow"></div>
        )}
        <div className="relative overflow-hidden">
          <div className="absolute -right-24 -top-20 size-44 overflow-hidden rounded-full bg-interactive-3"></div>
          <div className="absolute right-0 top-0 p-3">
            <EventDateDisplaySimple
              startDate={event.startDate}
              startTime={event.startTime}
              endDate={event.endDate}
              endTime={event.endTime}
              timezone={event.timeZone || "America/Los_Angeles"}
            />
          </div>
          <div className="flex w-full items-start gap-7 p-5">
            {props.variant === "card" && (
              <EventDetailsCard
                id={id}
                name={event.name!}
                image={image}
                startDate={event.startDate!}
                endDate={event.endDate!}
                startTime={event.startTime!}
                endTime={event.endTime!}
                timezone={event.timeZone || DEFAULT_TIMEZONE}
                location={event.location}
                description={event.description}
                visibility={visibility}
                noLinks={true}
              />
            )}
          </div>
        </div>
        <div className="p-3"></div>
      </Link>

      <div className="absolute bottom-2 left-2 z-10 flex gap-2">
        {user && (
          <UserAvatarMini
            username={user.username}
            displayName={user.displayName}
            userImage={user.userImage}
            isSupporter={user.isSupporter}
          />
        )}
      </div>

      <div className="absolute bottom-2 right-2 z-20">
        <EventActionButtons
          user={user}
          event={event as AddToCalendarButtonPropsRestricted}
          id={id}
          isOwner={!!isOwner}
          isFollowing={isFollowing}
          visibility={visibility}
          variant="minimal"
          size="sm"
        />
      </div>
    </li>
  );
}

export function EventPreview(
  props: EventListItemProps & { event: AddToCalendarCardProps },
) {
  const { id, event } = props;

  return (
    <div
      className={cn(
        "relative grid max-w-xl overflow-hidden rounded-xl bg-white p-7 shadow-sm after:pointer-events-none after:absolute after:left-0 after:top-0 after:size-full after:rounded-xl after:border after:border-neutral-3 after:shadow-sm",
      )}
    >
      <div className="absolute -right-24 -top-20 size-44 overflow-hidden rounded-full bg-interactive-3"></div>
      <div className="absolute right-0 top-0 p-3">
        <EventDateDisplaySimple
          startDate={event.startDate}
          startTime={event.startTime}
          endDate={event.endDate}
          endTime={event.endTime}
          timezone={event.timeZone || "America/Los_Angeles"}
        />
      </div>
      <div className="flex w-full items-start gap-7">
        <EventDetails
          preview
          id={id}
          name={event.name!}
          startDate={event.startDate!}
          endDate={event.endDate!}
          startTime={event.startTime!}
          endTime={event.endTime!}
          timezone={event.timeZone || "America/Los_Angeles"}
          location={event.location}
          description={event.description}
          visibility={"public"}
        />
      </div>
    </div>
  );
}

function DateAndTimeDisplay({
  happeningNow,
  endDateInfo,
  endTime,
  isClient,
  startDateInfo,
  startTime,
  variant = "default",
  eventTimezone,
  userTimezone,
  eventTimezoneStartDateInfo,
  eventTimezoneEndDateInfo,
}: {
  happeningNow?: boolean;
  endDateInfo: DateInfo;
  endTime?: string;
  isClient: boolean;
  startDateInfo: DateInfo;
  startTime?: string;
  variant?: "default" | "compact";
  eventTimezone?: string;
  userTimezone?: string;
  eventTimezoneStartDateInfo?: DateInfo;
  eventTimezoneEndDateInfo?: DateInfo;
}) {
  const normalizeTimezone = (tz?: string): string => {
    const val = tz?.trim().toLowerCase();
    return !val || val === "unknown" ? "" : val;
  };

  const normalizedEventTz = normalizeTimezone(eventTimezone);
  const normalizedUserTz = normalizeTimezone(userTimezone);

  const shouldShowTimezone =
    normalizedEventTz &&
    normalizedUserTz &&
    normalizedEventTz !== normalizedUserTz &&
    startTime;

  const resolveTZAbbr = (tz?: string): string | undefined => {
    try {
      if (!tz) return undefined;
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "short",
      }).formatToParts(new Date());
      const val = parts.find((p) => p.type === "timeZoneName")?.value;
      return val?.trim() ? val : undefined;
    } catch {
      return undefined;
    }
  };

  const timezoneAbbreviation: string | undefined = shouldShowTimezone
    ? resolveTZAbbr(eventTimezone)
    : undefined;

  return (
    <div className="flex flex-col gap-2 font-medium leading-none">
      {isClient && eventTimesAreDefined(startTime, endTime) && (
        <div
          className={cn(
            " text-sm uppercase text-neutral-2",
            variant === "compact" && "text-xs text-neutral-2 sm:flex-col",
          )}
          suppressHydrationWarning
        >
          <span>
            {startDateInfo.dayOfWeek.substring(0, 3)}{" "}
            {startDateInfo.monthName.substring(0, 3)} {startDateInfo.day}
            {shouldShowYear(startDateInfo) ? `, ${startDateInfo.year}` : ""}
            {" • "}
            {(() => {
              const fmt = (d: DateInfo) => {
                const h = d.hour % 12 || 12;
                const m =
                  d.minute === 0
                    ? ""
                    : `:${d.minute.toString().padStart(2, "0")}`;
                const p = d.hour < 12 ? "AM" : "PM";
                return { h, m, p };
              };
              const u1 = fmt(startDateInfo);
              const u2 = fmt(endDateInfo);
              const userStr =
                u1.p === u2.p
                  ? `${u1.h}${u1.m}–${u2.h}${u2.m}${u2.p}`
                  : `${u1.h}${u1.m}${u1.p}–${u2.h}${u2.m}${u2.p}`;

              if (timezoneAbbreviation && eventTimezoneStartDateInfo) {
                const e1 = fmt(eventTimezoneStartDateInfo);
                const e2 = eventTimezoneEndDateInfo
                  ? fmt(eventTimezoneEndDateInfo)
                  : undefined;
                const eventStr = e2
                  ? e1.p === e2.p
                    ? `${e1.h}${e1.m}–${e2.h}${e2.m}${e2.p}`
                    : `${e1.h}${e1.m}${e1.p}–${e2.h}${e2.m}${e2.p}`
                  : `${e1.h}${e1.m}${e1.p}`;
                return (
                  <>
                    {userStr}{" "}
                    <em>
                      ({eventStr} {timezoneAbbreviation})
                    </em>
                  </>
                );
              }
              return userStr;
            })()}
          </span>
          {happeningNow && (
            <span className="ml-1 rounded-full bg-yellow-100 px-1 text-yellow-700">
              Now!
            </span>
          )}{" "}
          {startTime && startDateInfo && (
            <HappeningSoonBadge startDateInfo={startDateInfo} />
          )}
        </div>
      )}
    </div>
  );
}

export function EventPage(props: EventPageProps) {
  const { user: clerkUser, isLoaded: isAuthLoaded } = useUser();

  const { user, eventFollows, id, event, image } = props;
  const roles = clerkUser?.publicMetadata.roles as string[] | undefined;
  const isSelf = clerkUser?.id === user?.id;
  const isOwner = isSelf || roles?.includes("admin");
  const isFollowing = !!eventFollows.find(
    (item) => clerkUser?.id === item.userId,
  );

  const {
    startDate,
    startTime,
    endDate,
    endTime,
    timeZone: timezone,
    location,
    description,
  } = event;

  const { timezone: userTimezone } = useContext(TimezoneContext);
  if (!startDate || !endDate) {
    console.error("startDate or endDate is missing");
    return null;
  }

  if (!timezone) {
    console.error("timezone is missing");
    return null;
  }

  const startDateInfo = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, userTimezone.toString())
    : getDateInfoUTC(startDate);
  const endDateInfo = endTime
    ? getDateTimeInfo(endDate, endTime, timezone, userTimezone.toString())
    : getDateInfoUTC(endDate);

  const eventTimezoneStartDateInfo = startTime
    ? getDateTimeInfo(startDate, startTime, timezone, timezone)
    : null;
  const eventTimezoneEndDateInfo = endTime
    ? getDateTimeInfo(endDate, endTime, timezone, timezone)
    : null;

  const normalizeTimezone = (tz?: string): string => {
    const val = tz?.trim().toLowerCase();
    return !val || val === "unknown" ? "" : val;
  };

  const normalizedEventTz = normalizeTimezone(timezone);
  const normalizedUserTz = normalizeTimezone(userTimezone.toString());

  const shouldShowTimezone =
    normalizedEventTz &&
    normalizedUserTz &&
    normalizedEventTz !== normalizedUserTz &&
    startTime;

  const resolveTZAbbr = (tz?: string): string | undefined => {
    try {
      if (!tz) return undefined;
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: tz,
        timeZoneName: "short",
      }).formatToParts(new Date());
      const val = parts.find((p) => p.type === "timeZoneName")?.value;
      return val?.trim() ? val : undefined;
    } catch {
      return undefined;
    }
  };

  const timezoneAbbreviation: string | undefined = shouldShowTimezone
    ? resolveTZAbbr(timezone)
    : undefined;

  if (!startDateInfo || !endDateInfo) {
    console.error("startDateInfo or endDateInfo is missing");
    return null;
  }

  const fmtTime = (d: DateInfo) => {
    const h = d.hour % 12 || 12;
    const m = d.minute === 0 ? "" : `:${d.minute.toString().padStart(2, "0")}`;
    const p = d.hour < 12 ? "AM" : "PM";
    return { h, m, p };
  };
  const userTimeStr = (() => {
    const u1 = fmtTime(startDateInfo);
    const u2 = fmtTime(endDateInfo);
    return u1.p === u2.p
      ? `${u1.h}${u1.m}–${u2.h}${u2.m}${u2.p}`
      : `${u1.h}${u1.m}${u1.p}–${u2.h}${u2.m}${u2.p}`;
  })();
  const eventTimeInZone =
    timezoneAbbreviation && eventTimezoneStartDateInfo
      ? (() => {
          const e1 = fmtTime(eventTimezoneStartDateInfo);
          const e2 = eventTimezoneEndDateInfo
            ? fmtTime(eventTimezoneEndDateInfo)
            : undefined;
          const eventStr = e2
            ? e1.p === e2.p
              ? `${e1.h}${e1.m}–${e2.h}${e2.m}${e2.p}`
              : `${e1.h}${e1.m}${e1.p}–${e2.h}${e2.m}${e2.p}`
            : `${e1.h}${e1.m}${e1.p}`;
          return `${eventStr} ${timezoneAbbreviation}`;
        })()
      : undefined;

  const longDate = `${startDateInfo.dayOfWeek}, ${startDateInfo.monthName} ${startDateInfo.day}${
    shouldShowYear(startDateInfo) ? `, ${startDateInfo.year}` : ""
  }`;

  const ownerFirstName =
    user?.displayName?.trim().split(/\s+/)[0] || user?.username;

  return (
    <>
      {user && isOwner && props.archivedAt ? (
        <div className="mx-auto mb-4 flex w-full max-w-3xl items-center gap-3 rounded-2xl bg-white p-4 shadow-sm">
          <Archive className="size-5 shrink-0 text-neutral-2" />
          <div className="flex-1 text-sm text-neutral-2">
            <span className="font-semibold text-neutral-1">Archived.</span>{" "}
            Hidden from your feed and calendar — anyone with the link can still
            view it.
          </div>
        </div>
      ) : null}
      <EventCard
        eventId={id}
        userUsername={user?.username}
        userName={user?.displayName || user?.username || ""}
        userDisplayName={user?.displayName}
        userAvatar={user?.userImage || ""}
        userEmoji={user?.emoji || ""}
        userIsSupporter={user?.isSupporter}
        hasUser={!!user}
        isPrivate={props.visibility === "private"}
        isOwner={!!(user && isOwner)}
        editHref={user && isOwner ? `/event/${id}/edit` : undefined}
        eventName={event.name || ""}
        eventShortDescription={event.shortDescription}
        eventDate={longDate}
        eventTime={startTime ? userTimeStr : undefined}
        eventTimeInZone={eventTimeInZone}
        eventLocation={location || ""}
        eventDescription={description || ""}
        eventImage={image || null}
        eventMetadata={props.eventMetadata}
        brand={
          isAuthLoaded && !clerkUser ? (
            <Link href="/" aria-label="Soonlist" className="flex items-center">
              <Logo variant="mark" className="size-7" />
            </Link>
          ) : undefined
        }
        calendarButton={
          <CalendarButton
            event={event as ATCBActionEventConfig}
            id={id}
            username={user?.username}
            userDisplayName={user?.displayName}
            type="button"
          />
        }
        shareButton={<ShareButton type="icon" event={event} id={id} />}
        followButton={
          user && !isSelf ? (
            <FollowEventButton eventId={id} following={isFollowing} />
          ) : undefined
        }
        editButton={
          user && isOwner ? (
            <EditButton type="icon" userId={user.id} id={id} />
          ) : undefined
        }
        deleteButton={
          user && isOwner ? (
            props.archivedAt ? (
              <>
                <RestoreButton type="icon" userId={user.id} id={id} />
                <DeleteButton type="icon" userId={user.id} id={id} />
              </>
            ) : (
              <ArchiveButton type="icon" userId={user.id} id={id} />
            )
          ) : undefined
        }
      />
      {isAuthLoaded && !isSelf && user?.username && !props.hideSubscribeCTA && (
        <>
          <div className="h-28" aria-hidden />
          <SubscribeCTA
            username={user.username}
            firstName={ownerFirstName ?? user.username}
            source="event_page"
          />
        </>
      )}
    </>
  );
}
